import { useEffect } from "react";
import {
  WhatsappLogo,
  Envelope,
  Phone,
  ChatText,
  Brain,
  Door,
  UserPlus,
  Users,
  Receipt,
  Sparkle,
  PencilLine,
} from "@phosphor-icons/react";
import { LIMIT_TYPES, LIMIT_MESSAGES, getPlanLimits } from "../../services/planService";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./PlanLimitModal.css";

/**
 * PlanLimitModal - Beautiful modal shown when user hits a plan limit
 *
 * Displays:
 * - Relevant icon for the limit type
 * - Clear explanation of the limitation
 * - Current usage vs limit
 * - Contact options for upgrade
 */
function PlanLimitModal({
  isOpen,
  onClose,
  limitType,
  current,
  limit,
  plan,
  therapistName,
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const limitInfo = LIMIT_MESSAGES[limitType] || {
    title: "הגעת למגבלה",
    description: "הגעת למגבלה בתוכנית הנוכחית שלך.",
    icon: "Sparkle",
  };

  const planLimits = getPlanLimits(plan);

  // Get the appropriate icon component
  const IconComponent = {
    ChatText,
    Brain,
    Door,
    UserPlus,
    Users,
    Receipt,
    Sparkle,
    PencilLine,
  }[limitInfo.icon] || Sparkle;

  // WhatsApp link with pre-filled message
  const whatsappNumber = "972543073737";
  const limitDescription = limitInfo.title.replace("הגעת ל", "").replace("אינה זמינה", "");
  const whatsappMessage = encodeURIComponent(
    `שלום! אני ${therapistName || "מטפל/ת"} מ-CliniKit. הגעתי למגבלה (${limitDescription}) ואשמח לשמוע על אפשרויות לשדרוג התוכנית.`
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // Email link
  const emailAddress = "office@clinikit.co.il";
  const emailSubject = encodeURIComponent("שדרוג תוכנית CliniKit");
  const emailBody = encodeURIComponent(
    `שלום,\n\nאני ${therapistName || "מטפל/ת"} בתוכנית ${planLimits.nameHebrew}.\nהגעתי למגבלה (${limitDescription}) ואשמח לשמוע על אפשרויות שדרוג.\n\nתודה!`
  );
  const emailLink = `mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`;

  // Format usage display
  const showUsage = current !== undefined && limit !== undefined && limit > 0;
  const usagePercentage = showUsage ? Math.round((current / limit) * 100) : 100;

  return (
    <div className="plan-limit-overlay" onClick={onClose}>
      <div className="plan-limit-modal" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose} />

        {/* Icon */}
        <div className="plan-limit-icon">
          <IconComponent size={32} weight="duotone" />
        </div>

        {/* Title */}
        <h2 className="plan-limit-title">{limitInfo.title}</h2>

        {/* Description */}
        <p className="plan-limit-description">{limitInfo.description}</p>

        {/* Usage indicator (if applicable) */}
        {showUsage && (
          <div className="plan-limit-usage">
            <div className="plan-limit-usage-bar">
              <div
                className="plan-limit-usage-fill"
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
            <div className="plan-limit-usage-text">
              <span className="plan-limit-usage-current">{current}</span>
              <span className="plan-limit-usage-separator">/</span>
              <span className="plan-limit-usage-limit">{limit}</span>
            </div>
          </div>
        )}

        {/* Plan badge */}
        <div className="plan-limit-plan-info">
          <span className="plan-limit-plan-label">התוכנית הנוכחית:</span>
          <span className={`plan-limit-plan-badge ${plan}`}>
            {planLimits.nameHebrew}
          </span>
        </div>

        {/* Upgrade message */}
        <div className="plan-limit-upgrade-message">
          <Sparkle size={18} weight="fill" className="plan-limit-sparkle" />
          <p>שדרוג התוכנית יאפשר לך להמשיך לעבוד ללא הפרעות</p>
        </div>

        {/* Contact buttons */}
        <div className="plan-limit-actions">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="plan-limit-btn plan-limit-btn-primary"
          >
            <WhatsappLogo size={22} weight="fill" />
            <span>שלח הודעה בוואטסאפ</span>
          </a>

          <a href={emailLink} className="plan-limit-btn plan-limit-btn-secondary">
            <Envelope size={20} weight="regular" />
            <span>שלח מייל</span>
          </a>
        </div>

        {/* Direct contact info */}
        <div className="plan-limit-contact-info">
          <div className="plan-limit-contact-item">
            <Phone size={16} weight="regular" />
            <span>054-3073737</span>
          </div>
          <span className="plan-limit-contact-divider">|</span>
          <div className="plan-limit-contact-item">
            <Envelope size={16} weight="regular" />
            <span>office@clinikit.co.il</span>
          </div>
        </div>

        {/* Footer */}
        <p className="plan-limit-footer">צוות CliniKit ישמח לעזור לך למצוא את התוכנית המתאימה</p>
      </div>
    </div>
  );
}

export default PlanLimitModal;
